import { Close as CloseIcon } from "@/components/icons";
import "./index.scss";

import { FC, useEffect } from "react";
import { createPortal } from "react-dom";

export interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  titleBadge?: React.ReactNode;
  headerIcon?: React.ReactNode;
  headerActions?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  width?: string;
  position?: "left" | "right";
  /** "dark" = koyu gri header (#2f333c) */
  headerVariant?: "default" | "dark";
}

const Drawer: FC<DrawerProps> = ({
  isOpen,
  onClose,
  title,
  titleBadge,
  headerIcon,
  headerActions,
  children,
  footer,
  width = "561px",
  position = "right",
  headerVariant = "default",
}) => {
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscapeKey);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div className="drawer-overlay" onClick={onClose}>
      <div
        className={`drawer drawer--${position}`}
        style={{ width }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`drawer__header ${headerVariant === "dark" ? "drawer__header--dark" : ""}`}>
          <div className="drawer__header-left">
            {headerIcon && (
              <span className="drawer__header-icon">{headerIcon}</span>
            )}
            {title && <h3 className="drawer__title">{title}</h3>}
            {titleBadge && (
              <span className="drawer__title-badge">{titleBadge}</span>
            )}
          </div>
          <div className="drawer__header-right">
            {headerActions}
            <button className="drawer__close" onClick={onClose}>
              <CloseIcon width={20} height={20} color={headerVariant === "dark" ? "#fff" : "#34314A"} />
            </button>
          </div>
        </div>

        <div className="drawer__content">{children}</div>

        {footer && <div className="drawer__footer">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
};

export default Drawer;
